import { useState } from "react";
import { Check, CreditCard, LogOut, Settings2, ShieldAlert, UserRound, X } from "lucide-react";
import {
  formatAllowance,
  openBillingPortal,
  redeemPromotion,
  revokeAllSessions,
  selectAccountPlan,
  startCheckout,
  updateAccountSettings,
  type DictaAccount
} from "../app/account";
import { JURISDICTION_PACKS } from "../domain/jurisdiction-packs";

export type AccountTab = "profile" | "preferences" | "billing" | "security";

type AccountPanelProps = {
  account: DictaAccount;
  initialTab?: AccountTab;
  onChange: (account: DictaAccount) => void;
  onClose: () => void;
  onSignOut: () => void;
};

const TABS: ReadonlyArray<{ id: AccountTab; label: string }> = [
  { id: "profile", label: "Profile" },
  { id: "preferences", label: "Preferences" },
  { id: "billing", label: "Billing" },
  { id: "security", label: "Security" }
];

const TAB_ICONS = {
  profile: UserRound,
  preferences: Settings2,
  billing: CreditCard,
  security: ShieldAlert
} as const;

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const parsed = new Date(iso);
  return Number.isNaN(parsed.valueOf())
    ? ""
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
};

const formatHours = (seconds: number) => {
  const hours = seconds / 3600;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
};

const formatHoursWithUnit = (seconds: number) => {
  const hours = formatHours(seconds);
  return `${hours} ${hours === "1" ? "hour" : "hours"}`;
};

const formatUsageReset = (periodKey: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
  if (!match) return null;
  const reset = new Date(Date.UTC(Number(match[1]), Number(match[2]), 1));
  return reset.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC"
  });
};

const collapsedPlanSummary = (plan: DictaAccount["plans"][number]) => {
  if (plan.allowancePeriod === "lifetime") {
    return `${Math.round(plan.allowanceSeconds / 60)} lifetime minutes · Reviewed PDF export · No certified transcript`;
  }
  return `Everything in Free, plus ${formatHoursWithUnit(plan.allowanceSeconds)}/month, secure delivery and priority processing · US$${plan.priceMonthlyUsd}/month, billed in USD · No certified transcript`;
};

export function AccountPanel({
  account,
  initialTab = "profile",
  onChange,
  onClose,
  onSignOut
}: AccountPanelProps) {
  const [tab, setTab] = useState<AccountTab>(initialTab);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState(account.user.name ?? "");
  const [promotionCode, setPromotionCode] = useState("");

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setMessage("");
    try {
      await action();
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "The change could not be saved."
      );
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = () =>
    run(async () => {
      onChange(await updateAccountSettings({ name: name.trim() || null }));
      setMessage("Profile saved.");
    });

  const savePreference = (
    update: Partial<DictaAccount["settings"]>
  ) =>
    run(async () => {
      onChange(await updateAccountSettings({ settings: update }));
      setMessage("Preferences saved.");
    });

  const subscription = account.billing.subscription;
  const subscribed = Boolean(
    subscription &&
      subscription.status &&
      !["canceled", "unpaid", "incomplete_expired"].includes(
        subscription.status
      )
  );

  const choosePlan = (planId: DictaAccount["user"]["plan"]) =>
    run(async () => {
      if (planId === account.user.plan) return;
      if (planId === "free") {
        onChange(await selectAccountPlan("free"));
        setMessage("You are back on the free plan.");
        return;
      }
      await startCheckout();
    });

  const percentage = account.usage.includedSeconds
    ? Math.min(
        100,
        Math.round((account.usage.usedSeconds / account.usage.includedSeconds) * 100)
      )
    : 0;

  const paidPlan = account.plans.find((plan) => plan.id === "paid");
  const hasRedeemedPromotion = account.promotions.redeemedCodes.length > 0;
  const resetDate = formatUsageReset(account.usage.periodKey);
  const orderedPlans = [...account.plans].sort((left, right) =>
    left.id === account.user.plan ? -1 : right.id === account.user.plan ? 1 : 0
  );

  return (
    <div className="modal-layer" role="presentation" onMouseDown={onClose}>
      <section
        className="account-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="account-panel__header">
          <div>
            <p className="overline">Settings</p>
            <h2 id="account-title">{account.user.name || account.user.email}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close settings"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <nav className="account-tabs" role="tablist" aria-label="Settings sections">
          {TABS.map(({ id, label }) => {
            const Icon = TAB_ICONS[id];
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={`account-tab-${id}`}
                aria-selected={tab === id}
                aria-controls={`account-tabpanel-${id}`}
                className={`account-tab ${tab === id ? "is-active" : ""}`}
                onClick={() => {
                  setTab(id);
                  setMessage("");
                }}
              >
                <Icon size={15} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </nav>

        {tab === "profile" && (
          <div
            className="account-section"
            role="tabpanel"
            id="account-tabpanel-profile"
            aria-labelledby="account-tab-profile"
          >
            <label className="field">
              <span>Display name</span>
              <input
                type="text"
                value={name}
                maxLength={120}
                placeholder="How your name appears on exports"
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input type="email" value={account.user.email} disabled readOnly />
            </label>
            <button
              className="button button--primary"
              type="button"
              onClick={() => void saveProfile()}
              disabled={busy || (account.user.name ?? "") === name.trim()}
            >
              {busy ? "Saving…" : "Save profile"}
            </button>
          </div>
        )}

        {tab === "preferences" && (
          <div
            className="account-section"
            role="tabpanel"
            id="account-tabpanel-preferences"
            aria-labelledby="account-tab-preferences"
          >
            <label className="field">
              <span>Default jurisdiction pack</span>
              <select
                value={account.settings.defaultJurisdictionPackId ?? ""}
                disabled={busy}
                onChange={(event) =>
                  void savePreference({
                    defaultJurisdictionPackId: event.target.value || null
                  })
                }
              >
                <option value="">No default (Global base)</option>
                {Object.values(JURISDICTION_PACKS).map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.name}
                  </option>
                ))}
              </select>
              <small>Pre-fills the jurisdiction on every new matter.</small>
            </label>
            <label className="field field--checkbox">
              <input
                type="checkbox"
                checked={account.settings.emailDeliveryReceipts}
                disabled={busy}
                onChange={(event) =>
                  void savePreference({
                    emailDeliveryReceipts: event.target.checked
                  })
                }
              />
              <span>Email me a receipt when a secure delivery is opened</span>
            </label>
            <label className="field field--checkbox">
              <input
                type="checkbox"
                checked={account.settings.autoDeleteAudioWithin24Hours}
                disabled={busy}
                onChange={(event) =>
                  void savePreference({
                    autoDeleteAudioWithin24Hours: event.target.checked
                  })
                }
              />
              <span>Automatically delete source audio within 24 hours</span>
            </label>
            <p className="account-panel__note">
              Applies only to recordings created after this preference is enabled.
              Deletion is irreversible, waits for successful transcription, and is
              blocked by a legal hold. Transcripts and audit records are retained.
            </p>
          </div>
        )}

        {tab === "billing" && (
          <div
            className="account-section"
            role="tabpanel"
            id="account-tabpanel-billing"
            aria-labelledby="account-tab-billing"
          >
            {subscription?.status === "past_due" && (
              <p className="form-message form-message--warning" role="alert">
                Your last payment failed. Update your card in Manage billing to
                keep full access.
              </p>
            )}
            {subscribed && subscription?.cancelAtPeriodEnd && (
              <p className="form-message" role="status">
                Your subscription ends on {formatDate(subscription.currentPeriodEnd)}.
                Dicta Pro access continues until then.
              </p>
            )}

            <section className="allowance-meter" aria-labelledby="allowance-title">
              <div className="allowance-meter__summary">
                <strong id="allowance-title">
                  {formatHours(account.usage.usedSeconds)} of{" "}
                  {formatHours(account.usage.includedSeconds)} hours used
                </strong>
                {resetDate ? <span>resets {resetDate}</span> : <span>lifetime allowance</span>}
              </div>
              <div
                className="allowance-meter__track"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={account.usage.includedSeconds}
                aria-valuenow={account.usage.usedSeconds}
              >
                <span
                  style={{ "--usage": `${percentage}%` } as React.CSSProperties}
                />
              </div>
              <small>{formatAllowance(account.usage.remainingSeconds)} remaining</small>
              {account.promotions.lifetimeBonusSeconds > 0 ? (
                <p className="allowance-meter__bonus" role="status">
                  +{formatHours(account.promotions.lifetimeBonusRemainingSeconds)} lifetime bonus{" "}
                  {formatHours(account.promotions.lifetimeBonusRemainingSeconds) === "1"
                    ? "hour"
                    : "hours"}{" "}
                  remaining ({account.promotions.redeemedCodes.join(", ")})
                </p>
              ) : null}
            </section>

            <section className="promotion-redemption" aria-labelledby="promotion-title">
              <div>
                <h3 id="promotion-title">Promotion code</h3>
                <p>
                  {hasRedeemedPromotion
                    ? "Redeem another eligible code for additional transcription time."
                    : "Redeem an eligible code for additional transcription time."}
                </p>
              </div>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(async () => {
                    const updated = await redeemPromotion(promotionCode);
                    onChange(updated);
                    setPromotionCode("");
                  });
                }}
              >
                <label className="field">
                  <span>Code</span>
                  <input
                    type="text"
                    value={promotionCode}
                    maxLength={32}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(event) => setPromotionCode(event.target.value)}
                  />
                </label>
                <button
                  className="button"
                  type="submit"
                  disabled={busy || !promotionCode.trim()}
                >
                  {busy ? "Redeeming…" : "Redeem code"}
                </button>
              </form>
            </section>

            <div className="plan-grid">
              {orderedPlans.map((plan) => {
                const active = account.user.plan === plan.id;
                if (!active) {
                  return (
                    <article className="plan-row" key={plan.id}>
                      <div>
                        <h3>{plan.name}</h3>
                        <p>{collapsedPlanSummary(plan)}</p>
                      </div>
                      {plan.id === "paid" ? (
                        <button
                          className="button button--primary"
                          type="button"
                          onClick={() => void choosePlan("paid")}
                          disabled={busy || !account.billing.checkoutEnabled}
                          title={
                            account.billing.checkoutEnabled
                              ? undefined
                              : "Billing is not available in this environment yet"
                          }
                        >
                          {busy ? "Opening Checkout…" : "Upgrade"}
                        </button>
                      ) : (
                        <button
                          className="button button--quiet"
                          type="button"
                          onClick={() =>
                            void (subscribed ? run(openBillingPortal) : choosePlan("free"))
                          }
                          disabled={busy}
                        >
                          {busy ? "Opening billing…" : "Downgrade to Free"}
                        </button>
                      )}
                    </article>
                  );
                }
                return (
                  <article
                    className="plan-card is-active"
                    key={plan.id}
                  >
                    <header>
                      <div>
                        <div className="plan-card__title">
                          <h3>{plan.name}</h3>
                          <span className="plan-badge">Current plan</span>
                        </div>
                        <p>{plan.summary}</p>
                      </div>
                      {plan.priceMonthlyUsd > 0 ? (
                        <strong className="plan-price">
                          US${plan.priceMonthlyUsd}
                          <small>/ month</small>
                          <small>Billed in USD</small>
                        </strong>
                      ) : null}
                    </header>
                    <ul>
                      {plan.features.map((feature) => (
                        <li
                          className={feature.included ? undefined : "is-unavailable"}
                          key={feature.label}
                        >
                          {feature.included ? (
                            <Check size={14} aria-hidden="true" />
                          ) : (
                            <X size={14} aria-hidden="true" />
                          )}
                          <span>
                            <strong>{feature.label}</strong>
                            <small>{feature.detail}</small>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>

            {account.billing.subscription && (
              <button
                className="button"
                type="button"
                onClick={() => void run(openBillingPortal)}
                disabled={busy}
              >
                <CreditCard size={15} aria-hidden="true" />
                Manage billing
              </button>
            )}
            {paidPlan && account.billing.mode === "test" && (
              <p className="form-message" role="status">
                Billing is running in Stripe test mode — no real charges.
              </p>
            )}
          </div>
        )}

        {tab === "security" && (
          <div
            className="account-section"
            role="tabpanel"
            id="account-tabpanel-security"
            aria-labelledby="account-tab-security"
          >
            <p>
              Signing out everywhere revokes every active session for this
              account on every device, including this one.
            </p>
            <button
              className="button button--danger"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await revokeAllSessions();
                  onSignOut();
                })
              }
            >
              <ShieldAlert size={15} aria-hidden="true" />
              Sign out everywhere
            </button>
          </div>
        )}

        {message ? (
          <p className="form-message" role="status">
            {message}
          </p>
        ) : null}

        <footer className="account-panel__footer">
          <button className="button button--text" type="button" onClick={onSignOut}>
            <LogOut size={15} aria-hidden="true" />
            Sign out
          </button>
        </footer>
      </section>
    </div>
  );
}
